import { Link, useLocation } from '@tanstack/react-router';
import { ChevronRight, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import type { ReactNode } from 'react';

import { useSidebar } from '@/components/ui/sidebar';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { BREADCRUMB_TOGGLE_LEFT_PX, CONTENT_ALIGN_PX, PAGE_HEADER_CLASS } from './layoutConstants';

const PAGE_NAMES: Record<string, string> = {
  '/content-tracker': 'Home',
  '/media-plan-approval': 'Plan Approval',
  '/about': 'Help & Support',
  '/notifications': 'Notifications',
  '/profile': 'Profile',
  '/admin/users': 'Users',
  '/dashboard': 'Dashboard',
  '/mockups': 'Mockups',
  '/asset-preview-prototype': 'Preview [Dev]',
  '/assets': 'Asset Library',
  '/assets/briefs': 'My Briefs',
};

interface BreadcrumbProps {
  actions?: ReactNode;
  suffix?: string;
  /** Overrides the page name derived from PAGE_NAMES — use for dynamic routes (e.g. campaign name) */
  title?: string;
  /** When true, the sidebar toggle is disabled (e.g. asset preview needs max width) */
  sidebarLocked?: boolean;
}

export function Breadcrumb({ actions, suffix, title, sidebarLocked = false }: BreadcrumbProps) {
  const location = useLocation();
  const { toggleSidebar, state } = useSidebar();
  const currentPageName = title ?? PAGE_NAMES[location.pathname] ?? 'Page';

  const SidebarIcon = state === 'expanded' ? PanelLeftClose : PanelLeftOpen;

  const inner = (
    // Padded to CONTENT_ALIGN_PX so the breadcrumb text sits on the same
    // vertical line as table text below it. The sidebar toggle is absolutely
    // positioned in the gutter to its left, rather than in the flow, because in
    // the flow it pushed the text out to ~66px and away from that line.
    <div
      className="relative flex items-center min-h-12 pt-1.5 gap-3 pr-4"
      style={{ paddingLeft: `${CONTENT_ALIGN_PX}px` }}
    >
      <div
        className="absolute top-1/2 -translate-y-1/2 flex items-center"
        style={{ left: `${BREADCRUMB_TOGGLE_LEFT_PX}px` }}
      >
        {sidebarLocked ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="p-1 rounded-md cursor-not-allowed">
                <PanelLeftOpen className="h-5 w-5 text-slate-300" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              Sidebar collapsed on preview pages for more space
            </TooltipContent>
          </Tooltip>
        ) : (
          <button onClick={toggleSidebar} className="p-1 rounded-md hover:bg-slate-100">
            <SidebarIcon className="h-5 w-5 text-slate-600" />
            <span className="sr-only">Toggle sidebar</span>
          </button>
        )}
      </div>

      {/* Left: breadcrumb trail */}
      <div className="flex items-center shrink-0">
        {/* Breadcrumb trail */}
        <nav className="flex items-center gap-2 text-lg whitespace-nowrap">
          <Link
            to="/content-tracker"
            className="font-medium text-[#0014cc] hover:text-[#0014cc]/80 transition-colors"
          >
            Content Tracker
          </Link>
          <ChevronRight className="h-4 w-4 text-slate-400" />
          <Link
            to={location.pathname}
            search={location.search}
            hash={location.hash}
            className="font-medium text-[#0014cc] hover:text-[#0014cc]/80 transition-colors"
          >
            {currentPageName}
            {suffix && (
              <span className="text-slate-400 font-normal text-sm ml-2 relative top-[-1px]">
                {suffix}
              </span>
            )}
          </Link>
        </nav>
      </div>

      {/* Right: Action buttons slot */}
      {actions && <div className="flex items-center gap-1.5 flex-1 min-w-0">{actions}</div>}
    </div>
  );

  // When used standalone (no actions), wrap with the shared sticky header chrome
  if (!actions) {
    return <div className={PAGE_HEADER_CLASS}>{inner}</div>;
  }

  // When used with actions (e.g. inside a page Header), render bare
  return inner;
}
